import React, { useCallback, useMemo } from "react";
import { Column } from "uikit";
import { useTranslation } from "react-i18next";
import { isNumber } from "lodash";

import { Order, Car } from "../../../../../../services";
import {
	useTypedDispatch,
	useTypedSelector,
} from "../../../../../../redux/store";
import getArchiveOrdersForChart from "../../../../../../redux/services/Order/getArchiveOrdersForChart";
import { generateTimePeriods } from "../../../../../../redux/reducers/Archives/Reports/charts/byOrders";
import {
	useOrderStatistics,
	useModelSubscribe,
	useCarParkFilterAccessControl,
	useTaxiServiceFilterAccessControl,
} from "../../../../../../hooks";
import binarySearch from "../../../../../../utils/binarySearch";
import { useAppContext } from "../../../../../../context";

import { Header, Content } from "./components";
import Chart from "./components/Content/Chart";

const Charts: React.FC = () => {
	const dispatch = useTypedDispatch();
	const { t } = useTranslation();
	const { dateFns } = useAppContext();
	const globalLang = useTypedSelector((state) => state.session.language);
	const {
		dateFrom,
		dateTo,
		filter,
		orders: ordersRedux,
		loading,
	} = useTypedSelector((state) => state.archiveReports.charts.byOrders);
	const { models: taxiServices } = useTypedSelector(
		(state) => state.taxiServices,
	);

	const { accessTaxiServicesIds } =
		useTaxiServiceFilterAccessControl<Order.Model>();
	const { subCarParkFilterAccess } =
		useCarParkFilterAccessControl<Order.Model>();

	const optionsCarService = useMemo<Car.SubscribeOptions>(() => {
		const result: Car.SubscribeOptions = {
			language: globalLang,
			taxiServiceIds: accessTaxiServicesIds,
		};

		return result;
	}, [accessTaxiServicesIds, globalLang]);
	const { models: CarSub } = useModelSubscribe(optionsCarService, Car);

	const dataAndAddPark = ordersRedux.map((order) => ({
		...order,
		carToOrder:
			order.carToOrder?.map((carItem) => {
				const idCar = carItem.car?.id;
				if (!idCar) return carItem;

				const findFullCar = CarSub.find((car) => car.id === idCar);
				if (!findFullCar) return carItem;

				return {
					...carItem,
					car: { ...carItem.car, park: findFullCar.park },
				};
			}) || [],
	}));

	const orders = subCarParkFilterAccess({
		models: dataAndAddPark,
		branchPath: "carToOrder",
		branchPath2: "car.park.id",
	});

	const {
		overOrdersArr,
		completedOrdersArr,
		unCompletedOrdersArr,
		unclosedOrdersArr,
		dispatcherErrorArr,
		clientRefusalArr,
		driverRefusalArr,
		noCarAvailableArr,
		calculationErrorArr,
		completedByTransferArr,
		completedFromTransferArr,
		completedByOwnDriverArr,
		ownCompletedOrdersArr,
		firstOrdersClientArr,
		firstOrdersDoneClientArr,
	} = useOrderStatistics(orders);

	const taxiServiceIds = useMemo(
		() =>
			// eslint-disable-next-line no-nested-ternary
			filter.taxiServices[0] !== "all"
				? (filter.taxiServices as number[])
				: filter.companies[0] !== "all"
				? taxiServices
						?.filter(
							(taxiService) =>
								isNumber(taxiService.company?.id) &&
								(filter.companies as Array<number>).includes(
									// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
									taxiService.company!.id,
								),
						)
						.map((taxiService) => taxiService.id)
				: undefined,
		[filter.companies, filter.taxiServices, taxiServices],
	);

	const orderTypes: Charts.OrderContentKeys[] = useMemo(
		() => [
			"overOrders",
			"completedOrders",
			"unCompletedOrders",
			"unclosedOrders",
			"ownCompletedOrders",
			"completedByOwnDriver",
			"driverRefusal",
			"clientRefusal",
			"noCarAvailable",
			"calculationError",
			"dispatcherError",
			"completedFromTransfer",
			"completedByTransfer",
			"firstOrdersClient",
			"firstOrdersDoneClient",
		],
		[],
	);

	const orderColors = useMemo<Record<Charts.OrderContentKeys, string>>(
		() => ({
			overOrders: "#63b898",
			completedOrders: "#4467a1",
			unCompletedOrders: "#f2ea91",
			unclosedOrders: "#a2d3da",
			ownCompletedOrders: "#ec9b39",
			completedByOwnDriver: "#f24c12",
			driverRefusal: "#707070",
			clientRefusal: "#1d7b63",
			noCarAvailable: "#f4c26c",
			calculationError: "#af0915",
			dispatcherError: "#ede796",
			completedFromTransfer: "#2b416a",
			completedByTransfer: "#4c98a8",
			firstOrdersClient: "#b5b5b5",
			firstOrdersDoneClient: "#4467a0",
		}),
		[],
	);

	const sortedColorValues = useMemo(
		() => orderTypes.map((orderType) => orderColors[orderType]),
		[orderTypes, orderColors],
	);

	const fetchOrders = useCallback(async () => {
		dispatch(
			getArchiveOrdersForChart({
				dateRange: {
					from: dateFrom,
					to: dateTo,
				},
				taxiServiceIds,
			}),
		);
	}, [dateFrom, dateTo, dispatch, taxiServiceIds]);

	const chartData = useMemo(() => {
		const timePeriods = generateTimePeriods(
			filter.dateRangeLiteral,
			filter.interval,
		);

		const orderObkects: Record<Charts.OrderContentKeys, Order.Model[]> = {
			overOrders: overOrdersArr,
			completedOrders: completedOrdersArr,
			unCompletedOrders: unCompletedOrdersArr,
			unclosedOrders: unclosedOrdersArr,
			ownCompletedOrders: ownCompletedOrdersArr,
			completedByOwnDriver: completedByOwnDriverArr,
			driverRefusal: driverRefusalArr,
			clientRefusal: clientRefusalArr,
			noCarAvailable: noCarAvailableArr,
			calculationError: calculationErrorArr,
			dispatcherError: dispatcherErrorArr,
			completedFromTransfer: completedFromTransferArr,
			completedByTransfer: completedByTransferArr,
			firstOrdersClient: firstOrdersClientArr,
			firstOrdersDoneClient: firstOrdersDoneClientArr,
		};

		const orderArrays = orderTypes.map(
			(orderType) => orderObkects[orderType],
		);

		const ordersData: Record<string, { label: string; data: number[] }> =
			orderTypes.reduce((acc, key) => {
				acc[key] = {
					label: `pages.mainPage.pages.archives.tabs.orders.bottomStatisticsField.${key}`,
					data: Array<number>(timePeriods.length).fill(0),
				};
				return acc;
			}, {} as Record<string, { label: string; data: number[] }>);

		orders.forEach((order) => {
			const timePeriodKey = binarySearch(
				timePeriods,
				new Date(dateFns.toZonedTime(order.createdAt)).getTime(),
			);
			orderTypes.forEach((orderType, index) => {
				const orderArray = orderArrays[index];
				if (orderArray.find((_order) => _order.id === order.id)) {
					++ordersData[orderType].data[timePeriodKey];
				}
			});
		});

		const ordersDataArray = Object.entries(ordersData)
			.map(([key, value]) => ({
				...value,
				key,
				data:
					filter.chartType === "linear"
						? value.data
						: value.data.map((v) => (v === 0 ? null : v)),
			}))
			.filter(
				(set) =>
					filter.chartType === "linear" ||
					set.data.some((v) => v !== null),
			);

		const data: Chart.OrderChartData = {
			timePeriods,

			sets: ordersDataArray,
		};

		return data;
	}, [
		calculationErrorArr,
		clientRefusalArr,
		completedByOwnDriverArr,
		completedByTransferArr,
		completedFromTransferArr,
		completedOrdersArr,
		dateFns,
		dispatcherErrorArr,
		driverRefusalArr,
		filter.chartType,
		filter.dateRangeLiteral,
		filter.interval,
		firstOrdersClientArr,
		firstOrdersDoneClientArr,
		noCarAvailableArr,
		orderTypes,
		orders,
		overOrdersArr,
		ownCompletedOrdersArr,
		unCompletedOrdersArr,
		unclosedOrdersArr,
	]);

	const localizedChartData = useMemo(
		() =>
			chartData && {
				timePeriods: chartData.timePeriods.map((tp) =>
					new Date(tp).toLocaleString(
						globalLang,
						filter.interval.format,
					),
				),
				sets: chartData.sets.map((set) => ({
					...set,
					label: t(set.label),
				})),
			},
		[chartData, globalLang, filter.interval.format, t],
	);

	return (
		<Column maxedHeight justify="stretch" align="center">
			<Header
				dateFrom={dateFrom}
				dateTo={dateTo}
				filter={filter}
				loading={loading}
				fetchOrders={fetchOrders}
			/>

			<Content
				loading={loading}
				localizedChartData={localizedChartData}
				chartType={filter.chartType}
				colors={sortedColorValues}
			/>
		</Column>
	);
};

declare namespace Charts {
	interface Props {}

	type OrderContentKeys =
		| "overOrders"
		| "completedOrders"
		| "unCompletedOrders"
		| "unclosedOrders"
		| "driverRefusal"
		| "clientRefusal"
		| "noCarAvailable"
		| "calculationError"
		| "dispatcherError"
		| "ownCompletedOrders"
		| "completedByOwnDriver"
		| "completedFromTransfer"
		| "completedByTransfer"
		| "firstOrdersClient"
		| "firstOrdersDoneClient";
}

export default Charts;
